// Reviews API
import { apiRequest } from './request.js';

export const reviewsAPI = {
    // Get reviews for a business
    getBusinessReviews: async (businessId) => {
        // Use standard DRF filter param instead of nested route
        return apiRequest(`/reviews/?business=${businessId}`, {
            requireAuth: false
        });
    },

    // Create a new review. Si vienen fotos (array de File), enviamos
    // multipart con review_photo_0..4 — el backend acepta hasta 5.
    createReview: async (businessId, reviewData, photos = []) => {
        const cleanPhotos = (photos || []).filter(f => f instanceof File).slice(0, 5);
        if (cleanPhotos.length === 0) {
            const data = { ...reviewData, business: businessId };
            return apiRequest(`/reviews/`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        }
        const form = new FormData();
        form.append('business', businessId);
        Object.entries(reviewData).forEach(([k, v]) => {
            if (v === null || v === undefined) return;
            form.append(k, typeof v === 'object' ? JSON.stringify(v) : v);
        });
        cleanPhotos.forEach((f, i) => form.append(`review_photo_${i}`, f));
        return apiRequest(`/reviews/`, { method: 'POST', body: form });
    },

    // Update an existing review (con fotos opcionales — el backend siempre
    // las agrega; no se reemplazan las existentes).
    updateReview: async (businessId, reviewId, reviewData, photos = []) => {
        const cleanPhotos = (photos || []).filter(f => f instanceof File).slice(0, 5);
        if (cleanPhotos.length === 0) {
            return apiRequest(`/reviews/${reviewId}/`, {
                method: 'PATCH',
                body: JSON.stringify(reviewData),
            });
        }
        const form = new FormData();
        Object.entries(reviewData).forEach(([k, v]) => {
            if (v === null || v === undefined) return;
            form.append(k, typeof v === 'object' ? JSON.stringify(v) : v);
        });
        cleanPhotos.forEach((f, i) => form.append(`review_photo_${i}`, f));
        return apiRequest(`/reviews/${reviewId}/`, { method: 'PATCH', body: form });
    },

    // Delete a review
    deleteReview: async (businessId, reviewId) => {
        return apiRequest(`/reviews/${reviewId}/`, {
            method: 'DELETE'
        });
    },

    // Get user's reviews
    getUserReviews: async () => {
        return apiRequest('/businesses/user-reviews/');
    }
};

// Leads API - Según API_DOC.md, los endpoints de leads son:
// GET /api/leads/ - Listar leads
// POST /api/leads/ - Crear lead (contactar negocio)
// GET /api/leads/{id}/ - Obtener lead
// POST /api/leads/{id}/mark_read/ - Marcar como leído
// POST /api/leads/{id}/update_status/ - Actualizar estado
// GET /api/leads/unread_count/ - Contador de no leídos
export const leadsAPI = {
    // Get all leads (for authenticated user). Pass { requireAuth: false } as second
    // arg to attempt a public request (used for the shared forum feed).
    getAll: async (params = {}, options = {}) => {
        const queryParams = new URLSearchParams(params).toString();
        const endpoint = queryParams ? `/leads/?${queryParams}` : '/leads/';
        return apiRequest(endpoint, options);
    },

    // Create a lead (contact a business)
    // No auth required — the lead belongs to the business, not the contactor
    create: async (leadData) => {
        return apiRequest('/leads/', {
            method: 'POST',
            body: JSON.stringify(leadData),
            requireAuth: false
        });
    },

    // Get single lead
    getById: async (leadId) => {
        return apiRequest(`/leads/${leadId}/`);
    },

    // Mark lead as read
    markRead: async (leadId) => {
        return apiRequest(`/leads/${leadId}/mark_read/`, {
            method: 'POST'
        });
    },

    // Update lead status
    updateStatus: async (leadId, status) => {
        return apiRequest(`/leads/${leadId}/update_status/`, {
            method: 'POST',
            body: JSON.stringify({ status })
        });
    },

    // Get unread count (for badge notifications)
    getUnreadCount: async () => {
        return apiRequest('/leads/unread_count/');
    },

    // Legacy aliases for backwards compatibility
    trackLead: async (businessId, contactMethod) => {
        return leadsAPI.create({
            business: businessId,
            contact_method: contactMethod
        });
    },

    getLeadHistory: async (params = {}) => {
        return leadsAPI.getAll(params);
    },

    getLeadStats: async () => {
        return leadsAPI.getUnreadCount();
    }
};

// Favorites API - with client-side caching to avoid N+1 queries
let favoritesCache = null;
let favoritesCacheTimestamp = null;
const FAVORITES_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// IDs cache + in-flight deduplication
let favoriteIdsCache = null;
let favoriteIdsCacheTimestamp = null;
let favoriteIdsInFlight = null; // shared Promise while a request is in progress

// --- Favoritos ---

export const favoritesAPI = {
    // Ahora soporta businessId (number) o un objeto { type: 'business'|'product'|'service'|'profile', id: ... }
    addFavorite: async (target, type = 'business') => {
        let body = {};
        if (typeof target === 'object') {
            if (target.type === 'business') body = { business_id: target.id };
            else if (target.type === 'product') body = { product_id: target.id };
            else if (target.type === 'service') body = { service_id: target.id };
            else if (target.type === 'profile') body = { profile_id: target.id };
        } else {
            body = { business_id: target };
        }

        const result = await apiRequest('/favorites/toggle/', {
            method: 'POST',
            body: JSON.stringify(body),
        });
        // Invalidar ambos caches
        favoritesCache = null;
        favoritesCacheTimestamp = null;
        favoriteIdsCache = null;
        favoriteIdsCacheTimestamp = null;
        favoriteIdsInFlight = null;
        return result;
    },

    removeFavorite: async (target, type = 'business') => {
        return favoritesAPI.addFavorite(target, type);
    },

    // Usado para la página de favoritos completa (datos de negocio incluidos)
    getFavorites: async (forceRefresh = false) => {
        if (!forceRefresh && favoritesCache && favoritesCacheTimestamp &&
            Date.now() - favoritesCacheTimestamp < FAVORITES_CACHE_TTL) {
            return favoritesCache;
        }
        const result = await apiRequest('/favorites/my_favorites/');
        favoritesCache = result;
        favoritesCacheTimestamp = Date.now();
        return result;
    },

    // Endpoint ligero: solo IDs. Cache TTL + deduplicación de in-flight.
    // Múltiples llamadas simultáneas comparten una única request HTTP.
    getFavoriteIds: async (forceRefresh = false) => {
        if (!forceRefresh && favoriteIdsCache && favoriteIdsCacheTimestamp &&
            Date.now() - favoriteIdsCacheTimestamp < FAVORITES_CACHE_TTL) {
            return favoriteIdsCache;
        }
        // Si ya hay una request en vuelo, reutilizarla
        if (favoriteIdsInFlight) {
            return favoriteIdsInFlight;
        }
        favoriteIdsInFlight = apiRequest('/favorites/ids/')
            .then(data => {
                favoriteIdsCache = data;
                favoriteIdsCacheTimestamp = Date.now();
                favoriteIdsInFlight = null;
                return data;
            })
            .catch(err => {
                favoriteIdsInFlight = null;
                throw err;
            });
        return favoriteIdsInFlight;
    },

    // Una sola llamada a /favorites/ids/ sirve para todos los tipos
    checkFavorite: async (id, type = 'business') => {
        try {
            const ids = await favoritesAPI.getFavoriteIds();
            const numId = Number(id);
            if (type === 'product') return ids.product_ids?.includes(numId) ?? false;
            if (type === 'service') return ids.service_ids?.includes(numId) ?? false;
            if (type === 'profile') return ids.profile_ids?.includes(numId) ?? false;
            return ids.business_ids?.includes(numId) ?? false;
        } catch (e) {
            return false;
        }
    },

    invalidateCache: () => {
        favoritesCache = null;
        favoritesCacheTimestamp = null;
        favoriteIdsCache = null;
        favoriteIdsCacheTimestamp = null;
        favoriteIdsInFlight = null;
    }
};
